import React, { useState, useEffect, useRef } from 'react';
import { Plus, Calendar, Users, Clock, Edit, Trash2, AlertCircle, Upload, Download, Mail, Phone, Copy, MessageSquare, BookMarked, FolderPlus, Eye, UserPlus, Archive, RotateCcw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { programsAPI, athletesAPI } from '../services/api';
import { templatesAPI } from '../services/api';
import { AthleteAvatar } from '../components/AthleteAvatar';
import { useToast } from '../hooks/use-toast';
import useSubscription from '../hooks/useSubscription';
import UpgradeBanner from '../components/UpgradeBanner';
import LimitReachedModal from '../components/LimitReachedModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Programs = () => {
  const [programs, setPrograms] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  // Template state
  const [templates, setTemplates] = useState([]);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [saveTemplateProgram, setSaveTemplateProgram] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [includeRoster, setIncludeRoster] = useState(false);
  const [useTemplateOpen, setUseTemplateOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [newProgramName, setNewProgramName] = useState('');
  const [cloneRoster, setCloneRoster] = useState(false);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [quickAddName, setQuickAddName] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  const {
    isPro,
    programsUsed,
    programsLimit,
    athletesUsed,
    athletesLimit,
    isAtProgramLimit,
    isAtAthleteLimit,
    isApproachingProgramLimit,
    shouldShowUpgradeBanner,
    canAddProgram,
    refresh: refreshSubscription
  } = useSubscription();
  
  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    schedule: '',
    time: '',
    coach: 'Coach Martinez',
    athleteCount: 0,
    color: '#0067B9'
  });

  useEffect(() => {
    fetchPrograms();
    fetchAthletes();
    fetchTemplates();
  }, [showArchived]);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      const response = await programsAPI.getAll({ include_archived: showArchived });
      setPrograms(response.data);
      refreshSubscription();
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast({ title: 'Error loading programs', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      const response = await athletesAPI.getAll();
      setAthletes(response.data);
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await templatesAPI.getAll();
      setTemplates(res.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!saveTemplateProgram || !templateName.trim()) return;
    setTemplateLoading(true);
    try {
      await templatesAPI.create({
        program_id: saveTemplateProgram.id,
        template_name: templateName.trim(),
        include_roster: includeRoster,
      });
      toast({ title: `Template "${templateName}" saved!` });
      setSaveTemplateOpen(false);
      setTemplateName('');
      setIncludeRoster(false);
      setSaveTemplateProgram(null);
      await fetchTemplates();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Failed to save template', variant: 'destructive' });
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleCreateFromTemplate = async () => {
    if (!selectedTemplate || !newProgramName.trim()) return;
    setTemplateLoading(true);
    try {
      const res = await templatesAPI.createProgram(selectedTemplate.id, {
        name: newProgramName.trim(),
        clone_roster: cloneRoster,
      });
      const msg = res.data.cloned_athletes > 0
        ? `Program created with ${res.data.cloned_athletes} athletes cloned!`
        : 'Program created from template!';
      toast({ title: msg });
      setUseTemplateOpen(false);
      setSelectedTemplate(null);
      setNewProgramName('');
      setCloneRoster(false);
      await fetchPrograms();
      await fetchAthletes();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Failed to create program', variant: 'destructive' });
    } finally {
      setTemplateLoading(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await templatesAPI.delete(templateId);
      toast({ title: 'Template deleted' });
      await fetchTemplates();
    } catch (err) {
      toast({ title: 'Failed to delete template', variant: 'destructive' });
    }
  };

  const handleQuickAddAthlete = async (programId) => {
    const name = quickAddName.trim();
    if (!name) return;
    setQuickAddLoading(true);
    try {
      const parts = name.split(/\s+/);
      const avatar = parts.map(p => p[0]?.toUpperCase() || '').join('');
      await athletesAPI.create({
        name,
        programs: [programId],
        avatar,
        email: '',
        phone: '',
        emergencyContact: '',
        dateOfBirth: '',
      });
      toast({ title: `${name} added!` });
      setQuickAddName('');
      await fetchAthletes();
      await fetchPrograms();
    } catch (err) {
      toast({ title: err.response?.data?.detail || 'Failed to add athlete', variant: 'destructive' });
    } finally {
      setQuickAddLoading(false);
    }
  };

  // Get athletes with emails for a specific program
  const getProgramEmails = (programId) => {
    const programAthletes = athletes.filter(a => a.programs?.includes(programId));
    return programAthletes.filter(a => a.email).map(a => a.email);
  };

  // Get athletes for a specific program
  const getProgramAthletes = (programId) => {
    return athletes
      .filter(a => a.programs?.includes(programId))
      .sort((a, b) => {
        const lastA = (a.name || '').split(' ').pop().toLowerCase();
        const lastB = (b.name || '').split(' ').pop().toLowerCase();
        return lastA.localeCompare(lastB);
      });
  };

  // Get athletes with phone numbers for a specific program
  const getProgramPhones = (programId) => {
    const programAthletes = athletes.filter(a => a.programs?.includes(programId));
    return programAthletes.filter(a => a.phone).map(a => ({ name: a.name, phone: a.phone }));
  };

  // Email all athletes in program
  const handleEmailEveryone = (program) => {
    const emails = getProgramEmails(program.id);
    if (emails.length === 0) {
      toast({ title: 'No email addresses found for athletes in this program', variant: 'destructive' });
      return;
    }
    const bccList = emails.join(',');
    const subject = encodeURIComponent(`[${program.name}] `);
    window.location.href = `mailto:?bcc=${bccList}&subject=${subject}`;
    toast({ title: `Opening email with ${emails.length} recipients in BCC` });
  };

  // Open SMS app with all program phone numbers
  const handleTextGroup = (program) => {
    const phones = getProgramPhones(program.id);
    if (phones.length === 0) {
      toast({ title: 'No phone numbers found for athletes in this program', variant: 'destructive' });
      return;
    }
    const cleanNumbers = phones.map(p => p.phone.replace(/[^\d+]/g, ''));
    const numberList = cleanNumbers.join(',');

    // iOS requires /open?addresses= format for multiple recipients
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const smsUrl = isIOS
      ? `sms:/open?addresses=${numberList}`
      : `sms:${numberList}`;

    window.location.href = smsUrl;
    toast({ title: `Opening text message with ${phones.length} recipients` });
  };

  const handleAddProgramClick = () => {
    if (!canAddProgram) {
      setShowLimitModal(true);
      return;
    }
    setIsDialogOpen(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check limit before creating
    if (!editingProgram && !canAddProgram) {
      setShowLimitModal(true);
      setIsDialogOpen(false);
      return;
    }
    
    try {
      if (editingProgram) {
        await programsAPI.update(editingProgram.id, formData);
        toast({ title: 'Program updated successfully!' });
      } else {
        await programsAPI.create(formData);
        toast({ title: 'Program created successfully!' });
      }
      
      await fetchPrograms();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving program:', error);
      // Check if it's a limit error from backend
      if (error.response?.status === 403 && error.response?.data?.detail?.includes('limit')) {
        setShowLimitModal(true);
        setIsDialogOpen(false);
      } else {
        toast({ title: 'Error saving program', variant: 'destructive' });
      }
    }
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setFormData(program);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await programsAPI.delete(id);
      toast({ title: 'Program deleted successfully!' });
      await fetchPrograms();
    } catch (error) {
      console.error('Error deleting program:', error);
      toast({ title: 'Error deleting program', variant: 'destructive' });
    }
  };

  const handleArchive = async (program) => {
    try {
      await programsAPI.archive(program.id);
      toast({ title: `"${program.name}" archived` });
      await fetchPrograms();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error archiving program', variant: 'destructive' });
    }
  };

  const handleRestore = async (program) => {
    try {
      await programsAPI.restore(program.id);
      toast({ title: `"${program.name}" restored` });
      await fetchPrograms();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error restoring program', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      sport: '',
      schedule: '',
      time: '',
      coach: 'Coach Martinez',
      athleteCount: 0,
      color: '#0067B9'
    });
    setEditingProgram(null);
  };

  const colorOptions = [
    { value: '#0067B9', label: 'Ocean Blue' },
    { value: '#CE1126', label: 'Red' },
    { value: '#007A3D', label: 'Green' },
    { value: '#FCD116', label: 'Gold' }
  ];

  const handleExportPrograms = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/csv/export-programs`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `programs_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast({ title: 'Programs exported successfully!' });
    } catch (error) {
      console.error('Export error:', error);
      toast({ title: 'Error exporting programs', variant: 'destructive' });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/csv/program-template`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'program_import_template.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Template download error:', error);
      toast({ title: 'Error downloading template', variant: 'destructive' });
    }
  };

  const handleImportPrograms = async () => {
    if (!importFile) {
      toast({ title: 'Please select a file', variant: 'destructive' });
      return;
    }

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await fetch(`${BACKEND_URL}/api/csv/import-programs`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Import failed');
      }

      setImportResult(result);
      
      if (result.imported_count > 0) {
        toast({ title: `Successfully imported ${result.imported_count} program(s)!` });
        fetchPrograms();
      }
    } catch (error) {
      console.error('Import error:', error);
      toast({ title: error.message || 'Error importing programs', variant: 'destructive' });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Limit Reached Modal */}
      <LimitReachedModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        type="programs"
        currentCount={programsUsed}
        limit={programsLimit}
      />

      {/* Upgrade Banner - shows when approaching or at limit */}
      {shouldShowUpgradeBanner && !bannerDismissed && (
        <UpgradeBanner
          programsUsed={programsUsed}
          programsLimit={programsLimit}
          athletesUsed={athletesUsed}
          athletesLimit={athletesLimit}
          isAtProgramLimit={isAtProgramLimit}
          isAtAthleteLimit={isAtAthleteLimit}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Programs</h1>
          <p className="text-gray-600 mt-1">
            Manage your sports training programs
            {!isPro && (
              <span className="text-sm ml-2 text-gray-500">
                ({programsUsed}/{programsLimit} used)
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={showArchived ? 'default' : 'outline'}
            onClick={() => setShowArchived(!showArchived)}
            className={showArchived ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-500 text-amber-600 hover:bg-amber-50'}
            data-testid="toggle-archived-btn"
          >
            <Archive className="h-4 w-4 mr-2" />
            {showArchived ? 'Showing Archived' : 'Show Archived'}
          </Button>
          {templates.length > 0 && (
            <Button
              variant="outline"
              style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
              onClick={() => setUseTemplateOpen(true)}
              data-testid="use-template-btn"
            >
              <FolderPlus className="h-4 w-4 mr-2" />
              From Template
            </Button>
          )}
          <Button 
            variant="outline" 
            style={{ borderColor: '#007A3D', color: '#007A3D' }}
            onClick={handleExportPrograms}
            data-testid="export-programs-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
            setIsImportDialogOpen(open);
            if (!open) {
              setImportFile(null);
              setImportResult(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" style={{ borderColor: '#0067B9', color: '#0067B9' }}>
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Import Programs</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    Upload a CSV file to import multiple programs at once.
                  </p>
                  <Button
                    variant="link"
                    onClick={handleDownloadTemplate}
                    className="p-0 h-auto text-blue-600"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download sample template
                  </Button>
                </div>
                <div>
                  <Label htmlFor="program-csv">Select CSV File</Label>
                  <Input
                    id="program-csv"
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="mt-1"
                  />
                </div>
                {importResult && (
                  <Alert variant={importResult.error_count > 0 ? 'warning' : 'default'}>
                    <AlertDescription>
                      Imported: {importResult.imported_count} program(s)
                      {importResult.error_count > 0 && (
                        <span className="text-orange-600"> | Errors: {importResult.error_count}</span>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleImportPrograms}
                    disabled={!importFile || importing}
                    style={{ backgroundColor: '#0067B9' }}
                  >
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            if (open && !canAddProgram) {
              setShowLimitModal(true);
              return;
            }
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                style={{ backgroundColor: '#0067B9' }} 
                className="hover:opacity-90"
                onClick={handleAddProgramClick}
                data-testid="add-program-button"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Program
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingProgram ? 'Edit Program' : 'Create New Program'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Program Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Soccer Training - U12"
                  required
                />
              </div>
              <div>
                <Label htmlFor="sport">Sport</Label>
                <Input
                  id="sport"
                  name="sport"
                  value={formData.sport}
                  onChange={handleInputChange}
                  placeholder="e.g., Soccer, Basketball"
                  required
                />
              </div>
              <div>
                <Label htmlFor="schedule">Schedule</Label>
                <Input
                  id="schedule"
                  name="schedule"
                  value={formData.schedule}
                  onChange={handleInputChange}
                  placeholder="e.g., Mon, Wed, Fri"
                  required
                />
              </div>
              <div>
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleInputChange}
                  placeholder="e.g., 4:00 PM - 5:30 PM"
                  required
                />
              </div>
              <div>
                <Label htmlFor="color">Color Theme</Label>
                <div className="flex space-x-2 mt-2">
                  {colorOptions.map(option => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: option.value })}
                      className={`w-10 h-10 rounded-full border-4 transition-all ${
                        formData.color === option.value ? 'border-gray-800 scale-110' : 'border-gray-300'
                      }`}
                      style={{ backgroundColor: option.value }}
                      title={option.label}
                    />
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDialogOpen(false);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" style={{ backgroundColor: '#0067B9' }}>
                  {editingProgram ? 'Update' : 'Create'} Program
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Programs Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading programs...</p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map(program => (
              <Card key={program.id} className={`hover:shadow-lg transition-all ${program.is_archived ? 'opacity-60 border-dashed border-amber-300' : ''}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-16 rounded-full"
                        style={{ backgroundColor: program.is_archived ? '#9CA3AF' : program.color }}
                      />
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {program.name}
                          {program.is_archived && (
                            <span className="text-xs font-normal bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`archived-badge-${program.id}`}>
                              Archived
                            </span>
                          )}
                        </CardTitle>
                        <p className="text-sm text-gray-600 mt-1">{program.sport}</p>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="h-4 w-4 mr-2" style={{ color: program.color }} />
                      {program.schedule}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Clock className="h-4 w-4 mr-2" style={{ color: program.color }} />
                      {program.time}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Users className="h-4 w-4 mr-2" style={{ color: program.color }} />
                      {program.athleteCount} Athletes
                    </div>

                    {/* View/Add Athletes Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      style={{ borderColor: program.color, color: program.color }}
                      onClick={() => setExpandedProgram(expandedProgram === program.id ? null : program.id)}
                      data-testid={`view-athletes-btn-${program.id}`}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      {expandedProgram === program.id ? 'Hide Athletes' : 'View Athletes'}
                    </Button>

                    {/* Expandable Athletes List */}
                    {expandedProgram === program.id && (
                      <div className="border rounded-lg overflow-hidden">
                        {getProgramAthletes(program.id).length > 0 ? (
                          <div className="divide-y max-h-52 overflow-y-auto">
                            {getProgramAthletes(program.id).map(athlete => (
                              <div key={athlete.id} className="flex items-center gap-3 p-2.5 hover:bg-gray-50">
                                <AthleteAvatar athlete={athlete} size="sm" style={{ backgroundColor: program.color }} />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-gray-800 truncate">{athlete.name}</p>
                                  {athlete.email && <p className="text-xs text-gray-400 truncate">{athlete.email}</p>}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="p-4 text-center text-sm text-gray-400">
                            No athletes assigned yet
                          </div>
                        )}
                        <div className="border-t p-2 bg-gray-50 space-y-2">
                          <form
                            onSubmit={(e) => { e.preventDefault(); handleQuickAddAthlete(program.id); }}
                            className="flex gap-2"
                          >
                            <input
                              type="text"
                              placeholder="Athlete name..."
                              value={expandedProgram === program.id ? quickAddName : ''}
                              onChange={(e) => setQuickAddName(e.target.value)}
                              className="flex-1 text-sm px-3 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                              data-testid={`quick-add-input-${program.id}`}
                            />
                            <Button
                              type="submit"
                              size="sm"
                              disabled={!quickAddName.trim() || quickAddLoading}
                              style={{ backgroundColor: program.color }}
                              className="text-xs shrink-0"
                              data-testid={`quick-add-btn-${program.id}`}
                            >
                              <UserPlus className="h-3.5 w-3.5 mr-1" />
                              {quickAddLoading ? '...' : 'Add'}
                            </Button>
                          </form>
                          <a
                            href="/athletes"
                            className="text-xs font-medium flex items-center justify-center gap-1 hover:underline text-gray-500"
                            data-testid={`manage-athletes-link-${program.id}`}
                          >
                            Full athlete management &rarr;
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {/* Communication Buttons */}
                    <div className="flex flex-wrap gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEmailEveryone(program)}
                        disabled={getProgramEmails(program.id).length === 0}
                        className="text-blue-600 border-blue-300 hover:bg-blue-50 text-xs"
                        title={`Email ${getProgramEmails(program.id).length} athletes`}
                      >
                        <Mail className="h-3 w-3 mr-1" />
                        Email ({getProgramEmails(program.id).length})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTextGroup(program)}
                        disabled={getProgramPhones(program.id).length === 0}
                        className="text-green-600 border-green-300 hover:bg-green-50 text-xs"
                        title={`Text message ${getProgramPhones(program.id).length} athletes`}
                        data-testid={`text-group-btn-${program.id}`}
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Text ({getProgramPhones(program.id).length})
                      </Button>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 pt-2">
                      {program.is_archived ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[80px] text-amber-600 border-amber-300 hover:bg-amber-50"
                            onClick={() => handleRestore(program)}
                            data-testid={`restore-btn-${program.id}`}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Restore
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-red-600 hover:text-red-700"
                            onClick={() => handleDelete(program.id)}
                            data-testid={`delete-btn-${program.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[80px]"
                            onClick={() => {
                              setSaveTemplateProgram(program);
                              setTemplateName(program.name + ' Template');
                              setSaveTemplateOpen(true);
                            }}
                            data-testid={`save-template-btn-${program.id}`}
                          >
                            <BookMarked className="h-4 w-4 mr-1" />
                            Template
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1"
                            onClick={() => handleEdit(program)}
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 text-amber-600 hover:text-amber-700 border-amber-300 hover:bg-amber-50"
                            onClick={() => handleArchive(program)}
                            data-testid={`archive-btn-${program.id}`}
                          >
                            <Archive className="h-4 w-4 mr-1" />
                            Archive
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {programs.length === 0 && !loading && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-gray-500">
                  {showArchived ? (
                    <>
                      <Archive className="h-12 w-12 mx-auto mb-4 text-amber-400" />
                      <p className="text-lg font-medium">No archived programs</p>
                      <p className="text-sm mt-1">Programs you archive will appear here</p>
                    </>
                  ) : (
                    <>
                      <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-lg font-medium">No programs yet</p>
                      <p className="text-sm mt-1">Create your first program to get started</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Save as Template Dialog */}
      <Dialog open={saveTemplateOpen} onOpenChange={setSaveTemplateOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save as Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              Save <strong>{saveTemplateProgram?.name}</strong>'s settings as a reusable template.
            </p>
            <div>
              <label className="text-sm font-medium text-gray-700">Template Name</label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Fall Basketball Template"
                data-testid="template-name-input"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeRoster}
                onChange={(e) => setIncludeRoster(e.target.checked)}
                className="rounded border-gray-300"
                data-testid="include-roster-checkbox"
              />
              <span className="text-sm text-gray-700">Include athlete roster ({saveTemplateProgram ? athletes.filter(a => a.programs?.includes(saveTemplateProgram.id)).length : 0} athletes)</span>
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSaveTemplateOpen(false)}>Cancel</Button>
            <Button
              style={{ backgroundColor: '#7C3AED' }}
              disabled={!templateName.trim() || templateLoading}
              onClick={handleSaveTemplate}
              data-testid="save-template-confirm-btn"
            >
              {templateLoading ? 'Saving...' : 'Save Template'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create from Template Dialog */}
      <Dialog open={useTemplateOpen} onOpenChange={setUseTemplateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Program from Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {!selectedTemplate ? (
              <>
                <p className="text-sm text-gray-600">Choose a template to start with:</p>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {templates.map(t => (
                    <div
                      key={t.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => {
                        setSelectedTemplate(t);
                        setNewProgramName(t.name.replace(' Template', ''));
                        setCloneRoster(t.include_roster);
                      }}
                      data-testid={`template-option-${t.id}`}
                    >
                      <div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color || '#0067B9' }} />
                          <span className="font-medium text-sm">{t.name}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {t.sport} &middot; {t.schedule} &middot; {t.time}
                          {t.include_roster && ` · ${t.athlete_ids?.length || 0} athletes`}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-700 h-8 w-8 p-0"
                        onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(t.id); }}
                        data-testid={`delete-template-${t.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <button
                  onClick={() => setSelectedTemplate(null)}
                  className="text-sm text-blue-600 hover:underline"
                >&larr; Back to templates</button>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: selectedTemplate.color || '#0067B9' }} />
                    <span className="font-medium text-sm">{selectedTemplate.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{selectedTemplate.sport} &middot; {selectedTemplate.schedule} &middot; {selectedTemplate.time}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">New Program Name</label>
                  <input
                    type="text"
                    className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={newProgramName}
                    onChange={(e) => setNewProgramName(e.target.value)}
                    placeholder="e.g. Fall 2026 Basketball"
                    data-testid="new-program-name-input"
                  />
                </div>
                {selectedTemplate.include_roster && selectedTemplate.athlete_ids?.length > 0 && (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cloneRoster}
                      onChange={(e) => setCloneRoster(e.target.checked)}
                      className="rounded border-gray-300"
                      data-testid="clone-roster-checkbox"
                    />
                    <span className="text-sm text-gray-700">Clone athlete roster ({selectedTemplate.athlete_ids.length} athletes)</span>
                  </label>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setUseTemplateOpen(false)}>Cancel</Button>
                  <Button
                    style={{ backgroundColor: '#7C3AED' }}
                    disabled={!newProgramName.trim() || templateLoading}
                    onClick={handleCreateFromTemplate}
                    data-testid="create-from-template-btn"
                  >
                    {templateLoading ? 'Creating...' : 'Create Program'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Programs;